///
/// EarthBrowser
///
/// Created by Matt Giger on 12/31/2023
/// Copyright (c) 2023 Geodata Labs
///

import { EarthTile } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTile"
import { EarthTileRenderer } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTileRenderer"
import { EarthTileUpdateTask } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTileUpdateTask"
import { TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { type TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { registerTileStatsProvider } from "@/earthbrowser/utils/tileStatsRegistry"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import type { Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { sphere } from "@stovokor/math"
import { Texture } from "@stovokor/shader/Texture"
import { EarthCameraAdapter } from "./gesture-handlers/EarthCameraAdapter"
import { WebMercatorOverlayTileRenderer } from "./tilecache/WebMercatorOverlayTileRenderer"

const cLevelSetBase = 2
const cEarthBase = "https://tiles.earthbrowser.com/static/images/earthbase.jpg"
// NodeBase.renderOrder range for the radar refinement tiles: see render()
const cWmTileBaseOrder = -0.5
const cWmTileLevelOrderStep = 0.001

/**
 * Earth - The main Earth globe scene node
 *
 * Extends GestureNodeBase to integrate with the unified gesture and focus system.
 * As the viewport/default handler, Earth receives:
 * - Zoom events (wheel + pinch-to-zoom) when focused
 * - Touch gestures when touches hit the Earth surface
 * - Hover events when the cursor is over the Earth
 */
export class Earth extends GestureNode {
	public available: boolean = true
	public readonly id = "earth"
	public readonly focusable = true

	private earthTexture: Texture
	public overlays: TextureOverlay[] = []
	public cacheTask: EarthTileUpdateTask
	public intersectingSphere: sphere = new sphere(wgs.a)

	public cameraAdapter: EarthCameraAdapter

	constructor(context: EarthBrowserContext) {
		super(context)

		this.cacheTask = new EarthTileUpdateTask(context, cLevelSetBase)
		this.earthTexture = new Texture(context)

		this.cameraAdapter = new EarthCameraAdapter(context, this)

		// Load the base layer
		const imgLoader = new ImageLoader(cEarthBase)
		imgLoader.loadIntoTexture(this.earthTexture).catch(_e => undefined)
		this.buildBaseTiles(cLevelSetBase - 1)

		// Dev-only tile debugging via window.__ebDebug.getTileStats()
		registerTileStatsProvider(() => {
			const tiles = this.cacheTask.renderingTiles
			const byLevel: Record<number, number> = {}
			tiles.forEach(tile => { byLevel[tile.tileId.z] = (byLevel[tile.tileId.z] ?? 0) + 1 })
			const wmSeries = this.cacheTask.wmTileSeries
			return {
				rendered: tiles.length,
				byLevel,
				tiles: tiles.map(tile => ({
					key: tile.tileId.key,
					wanted: tile.wanted,
					sections: tile.sectionsToRender,
					loaded: tile.material.textureLoaded,
					pending: tile.material.loadPending,
					fade: tile.material.textureFade,
					fadingOut: tile.material.fadingOut,
					resolution: tile.geometry.resolution,
					night: tile.material.nightSource
				})),
				// Web Mercator radar refinement tiles
				wm: {
					hasFrames: wmSeries.hasFrames,
					frameCount: wmSeries.allFrames.length,
					currentTemplate: wmSeries.currentUrlTemplate,
					blend: wmSeries.blendFactor,
					// the mosaic's flow field for the current pair is ready and being applied
					flow: wmSeries.flowTexture() !== undefined,
					tiles: this.cacheTask.wmTileCount,
					renderable: this.cacheTask.wmRenderingTiles.map(t => t.tileId.key),
					// rendering tiles still fetching a frame or fading in from their parent
					pending: this.cacheTask.wmRenderingTiles.filter(t => t.material.loadPending || (t.material.textureFade.value ?? 0) > 0).length
				}
			}
		})
	}

	override destroy(): void {
		this.earthTexture.destroy()
		this.cacheTask.allTiles.forEach(tile => tile.destroy())
		super.destroy()
	}

	public get renderReady(): boolean {
		return this.earthTexture.available
	}

	// ========== GestureNodeBase Overrides ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)
		// this.context.logger.log("[Earth] Focus gained")
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		// this.context.logger.log("[Earth] Focus lost")
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	/**
	 * Get focus transition configuration for animating camera to Earth view
	 * Returns a dynamic target that tracks current Earth camera state
	 */
	public focusTransition(_camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getEarthCameraState(),
			params: {
				duration: 1.0,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		switch (command) {
			case "compass/reset":
				this.cameraAdapter.animatedTiltAndBearing(0, 0)
				return true

			case "compass/setTiltBearing":
				if (data && typeof data === "object" && "tilt" in data && "bearing" in data) {
					const { tilt, bearing } = data as { tilt: number; bearing: number }
					return this.cameraAdapter.animatedTiltAndBearing(tilt, bearing)
				}
				return false

			case "camera/setBearing":
				if (typeof data === "number") {
					return this.cameraAdapter.animatedCameraBearing(data)
				}
				return false

			case "camera/setTilt":
				if (typeof data === "number") {
					return this.cameraAdapter.animatedCameraTilt(data)
				}
				return false

			case "camera/setTiltImmediate":
				if (typeof data === "number") {
					this.cameraAdapter.immediateCameraTilt(data)
					return true
				}
				return false

			case "camera/setBearingImmediate":
				if (typeof data === "number") {
					this.cameraAdapter.animatedCameraBearing(data)	// Do it animated
					return true
				}
				return false

			case "zoom/in":
				return this.cameraAdapter.animatedZoomIn()

			case "zoom/out":
				return this.cameraAdapter.animatedZoomOut()

			case "zoom/set":
				if (typeof data === "number") {
					return this.cameraAdapter.animatedZoomTo(data)
				}
				return false

			case "flyTo/location":
				if (data && typeof data === "object" && "lat" in data && "lon" in data) {
					const { lat, lon, altitude } = data as { lat: number; lon: number; altitude?: number }
					return this.cameraAdapter.animatedFlyToLocation(lat, lon, altitude)
				}
				return false

			default:
				this.context.logger.log(`[Earth] Unknown command: ${command}`)
				return false
		}
	}


	public addOverlay(overlay: TextureOverlay): boolean {
		if (this.overlays.includes(overlay)) {
			return false
		}
		this.overlays.push(overlay)
		this.resetRenderers()
		return true
	}

	public removeOverlay(overlay: TextureOverlay): boolean {
		if (!this.overlays.includes(overlay)) {
			return false
		}
		this.overlays = this.overlays.filter(o => o !== overlay)
		this.resetRenderers()
		return true
	}

	public resetRenderers(): void {
		// Reset every cached tile, not just the ones in the current render
		// list: renderers bake the overlay materials and shader defines at
		// construction, so a cached tile (fully covered by children, over the
		// render cap, or dormant) that kept its renderer would resurrect
		// removed overlays when it re-enters the render list
		this.cacheTask.allEarthTiles.forEach(tile => {
			tile.renderer = undefined
		})
	}

	private buildBaseTiles(level: number): void {
		const rows = 1 << level
		const cols = 2 * rows
		const context = this.context as EarthBrowserContext
		for (let row = 0; row < rows; row++) {
			for (let col = 0; col < cols; col++) {
				const tile = new EarthTile(context, new TileId(level, row, col), this.earthTexture)
				this.cacheTask.addBaseTile(tile)
			}
		}
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.intersectingSphere.intersect(state.ray)
		if (dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.earthTexture.available) {
			return []
		}

		this.overlays.forEach(o => o.frameUpdate?.(state))

		// Tiles sort by the view depth of their center: opaque near-to-far
		// for early depth rejection, transparent overlay tiles far-to-near
		let actions: RenderActionSet[] = []
		this.cacheTask.renderingTiles.forEach(tile => {
			tile.renderer ??= new EarthTileRenderer(this.context, tile, this.overlays.map(o => o.overlayMaterial()))
			actions.push(tile.renderer.render(state, state.viewDepthOf(tile.center)))
		})

		// Web Mercator overlay tiles (radar refinement): advance the time
		// series, update materials, and draw loaded tiles over the globe
		const wmSeries = this.cacheTask.wmTileSeries
		if (wmSeries.hasFrames) {
			wmSeries.updateState(state)
			this.cacheTask.updateWmMaterials()
			// A parent tile keeps rendering under its children (they fade in
			// over it), and the premultiplied blend only resolves to the finer
			// tile if that one draws last, so order coarse-to-fine by level
			// rather than by depth. The whole group sits after the sky
			// (orders -4..-1) and before other transparent layers: the radar
			// lies on the surface, so clouds, cones and fronts blend over it
			this.cacheTask.wmRenderingTiles.forEach(tile => {
				tile.renderer ??= new WebMercatorOverlayTileRenderer(this.context, tile)
				actions.push(tile.renderer.render(state, state.viewDepthOf(tile.center), cWmTileBaseOrder + tile.tileId.z * cWmTileLevelOrderStep))
			})
		}

		state.pushTransform(this.transform)
		actions = actions.concat(this.renderChildren(state))
		state.popTransform()

		return actions
	}
}
